import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  input,
  output,
  QueryList,
  signal,
  untracked,
  ViewChildren,
} from '@angular/core';

import { optimizeCatalogImage } from '@shared/utils/catalog-image.utils';

import { CATALOG_IMAGE_MAX_SOURCE_SIZE_BYTES, MAX_IMAGE_FOR_PRODUCT_COUNT } from '@constants/constants';

export interface UploadedImage {
  file: File | null;
  url: string;
  fileSize?: number | null;
}

@Component({
  selector: 'app-image-uploader',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './image-uploader.component.html',
  styleUrl: './image-uploader.component.css',
})
export class ImageUploaderComponent implements AfterViewInit {
  private readonly maxFileSizeBytes = 5 * 1024 * 1024;

  public readonly maxImages = input<number>(MAX_IMAGE_FOR_PRODUCT_COUNT);
  public readonly simple = input<boolean>(false);
  public readonly optimizeForCatalog = input<boolean>(false);
  public readonly initialUrls = input<
    Array<{ url: string; fileSize?: number | null }> | string[] | null | undefined
  >();

  public readonly imagesChange = output<UploadedImage[]>();

  public readonly buttonMode = input<boolean>(false);

  public readonly isDragging = signal<boolean>(false);
  public readonly previewImages = signal<UploadedImage[]>([]);
  public readonly errorMessage = signal<string | null>(null);
  public readonly isOptimizing = signal<boolean>(false);

  @ViewChildren('positionSelect')
  private positionSelects!: QueryList<ElementRef<HTMLSelectElement>>;

  constructor() {
    effect(() => {
      const safe = this.initialUrls() ?? [];
      const existing = safe.map((item) =>
        typeof item === 'string'
          ? { file: null, url: item, fileSize: null }
          : { file: null, url: item.url, fileSize: item.fileSize ?? null },
      );
      const fromFiles = untracked(this.previewImages).filter((img) => img.file !== null);
      this.previewImages.set([...existing, ...fromFiles]);
    });
  }

  public get positionOptions(): number[] {
    return Array.from({ length: this.previewImages().length }, (_, i) => i + 1);
  }

  public get isFull(): boolean {
    return this.previewImages().length >= this.maxImages();
  }

  public get totalSizeLabel(): string | null {
    const images = this.previewImages();

    if (images.length === 0) {
      return null;
    }

    const totalBytes = images.reduce((sum, img) => sum + (img.file?.size ?? img.fileSize ?? 0), 0);

    if (totalBytes === 0) {
      return null;
    }

    if (totalBytes < 1024) {
      return `${totalBytes} B`;
    }

    if (totalBytes < 1024 * 1024) {
      return `${(totalBytes / 1024).toFixed(1)} KB`;
    }

    return `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  ngAfterViewInit(): void {
    this.positionSelects.changes.subscribe(() => this.syncSelectValues());
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(true);
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);

    const files = event.dataTransfer?.files;

    if (files) {
      this.processFiles(files);
    }
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files) {
      this.processFiles(input.files);
    }

    input.value = '';
  }

  public formatFileSize(img: UploadedImage): string | null {
    const bytes = img.file?.size ?? img.fileSize ?? null;

    if (!bytes) {
      return null;
    }

    if (bytes < 1024) {
      return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  public removeImage(index: number): void {
    this.previewImages.update(images =>
      images.filter((_, i) => i !== index),
    );

    this.emitImages();
  }

  public onPositionChange(currentIndex: number, event: Event): void {
    const select = event.target as HTMLSelectElement;
    const targetIndex = Number(select.value) - 1;

    if (
      Number.isNaN(targetIndex) ||
      targetIndex === currentIndex ||
      targetIndex < 0 ||
      targetIndex >= this.previewImages().length
    ) {
      return;
    }

    this.previewImages.update((images) => {
      const next = [...images];
      [next[currentIndex], next[targetIndex]] = [next[targetIndex], next[currentIndex]];
      return next;
    });

    this.syncSelectValues();
    this.emitImages();
  }

  private syncSelectValues(): void {
    // eslint-disable-next-line
    queueMicrotask(() => {
      this.positionSelects?.forEach((ref, idx) => {
        ref.nativeElement.value = String(idx + 1);
      });
    });
  }

  private async processFiles(files: FileList | File[]): Promise<void> {
    if (this.isOptimizing()) {
      return;
    }

    this.errorMessage.set(null);

    const currentImages = this.previewImages();

    const remainingSlots = this.maxImages() - currentImages.length;

    if (remainingSlots <= 0) {
      this.errorMessage.set(`Puedes subir un máximo de ${this.maxImages()} imágenes.`);
      return;
    }

    const selectedFiles = Array.from(files);
    const validImageFiles: File[] = [];

    const maxSourceSizeBytes = this.optimizeForCatalog() ? CATALOG_IMAGE_MAX_SOURCE_SIZE_BYTES : this.maxFileSizeBytes;

    selectedFiles.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        this.errorMessage.set('Solo puedes subir archivos de imagen.');
        return;
      }

      if (file.size > maxSourceSizeBytes) {
        this.errorMessage.set(`Cada imagen debe pesar máximo ${maxSourceSizeBytes / (1024 * 1024)}MB.`);
        return;
      }

      validImageFiles.push(file);
    });

    const filesToProcess = validImageFiles.slice(0, remainingSlots);

    if (validImageFiles.length > remainingSlots) {
      this.errorMessage.set(`Solo puedes agregar ${remainingSlots} imagen(es) más.`);
    }

    const readyFiles = this.optimizeForCatalog() ? await this.optimizeFiles(filesToProcess) : filesToProcess;

    readyFiles.forEach(file => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        const result = e.target?.result;

        if (typeof result !== 'string') {
          return;
        }

        this.previewImages.update(images => [
          ...images,
          {
            file,
            url: result,
            fileSize: file.size,
          },
        ]);

        this.emitImages();
      };

      reader.readAsDataURL(file);
    });
  }

  private async optimizeFiles(files: File[]): Promise<File[]> {
    this.isOptimizing.set(true);

    try {
      const optimizedFiles: File[] = [];

      for (const file of files) {
        const optimizedFile = await optimizeCatalogImage(file).catch(() => null);

        if (!optimizedFile) {
          this.errorMessage.set(`No pudimos optimizar "${file.name}" a WebP de máximo 200 KB. Prueba con otra imagen o desde otro navegador.`);
          continue;
        }

        optimizedFiles.push(optimizedFile);
      }

      return optimizedFiles;
    } finally {
      this.isOptimizing.set(false);
    }
  }

  private emitImages(): void {
    this.imagesChange.emit(
      this.previewImages(),
    );
  }
}
